import { cloneElement, createContext, isValidElement, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ButtonHTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { fmtDate, fmtNum, parseBR, parseNum, normalize } from '../format';
import { errorText } from '../api';
import { tipFor } from './tips';

/* ---------- Ícones (traço simples, 24x24) ---------- */
const PATHS: Record<string, string> = {
  home: 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  folder: 'M3 6a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z',
  tree: 'M4 4h6v4H4zM14 10h6v4h-6zM14 17h6v4h-6zM7 8v11h7M7 12h7',
  layers: 'M12 3l9 5-9 5-9-5zM3 13l9 5 9-5',
  gantt: 'M4 5h8M8 10h9M6 15h6M12 20h8M3 3v18',
  users: 'M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM22 19v-1a4 4 0 0 0-3-3.9M16 4.1a3 3 0 0 1 0 5.8',
  building: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M8 8h3M8 12h3M8 16h3M2 21h20',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  menu: 'M4 6h16M4 12h16M4 18h16',
  plus: 'M12 5v14M5 12h14',
  x: 'M6 6l12 12M18 6L6 18',
  history: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2',
  upload: 'M12 16V4M7 9l5-5 5 5M4 20h16',
  sun: 'M12 4V2M12 22v-2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M5 19l1.5-1.5M17.5 6.5L19 5M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10z',
  moon: 'M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z',
  logout: 'M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4M10 17l5-5-5-5M15 12H3',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  edit: 'M4 20h4L19 9l-4-4L4 16zM14 6l4 4',
  flag: 'M5 21V4M5 4h11l-2 4 2 4H5',
  check: 'M5 12l5 5 9-10',
  calendar: 'M4 6h16v15H4zM4 10h16M8 3v4M16 3v4',
  file: 'M6 3h8l5 5v13H6zM14 3v5h5M9 13h7M9 17h7',
  trail: 'M5 4v16M5 6h9l-2 3 2 3H5M15 20l2-2 3 3',
  report: 'M5 3h14v18H5zM8 16v-3M12 16V9M16 16v-5',
  money: 'M3 6h18v12H3zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 9v.01M18 15v.01',
  measure: 'M4 19h16M4 19V5M8 15l3-4 3 2 5-6',
  alert: 'M12 3l10 18H2zM12 10v5M12 18v.01',
  swap: 'M7 4L3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0'
};
export function Icon({ name }: { name: keyof typeof PATHS | string }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={PATHS[name] ?? ''} /></svg>;
}

/* ---------- Toasts ---------- */
type Toast = { id: number; text: string; err?: boolean };
const ToastCtx = createContext<{ ok: (t: string) => void; err: (e: unknown) => void }>({ ok: () => {}, err: () => {} });
export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<Toast[]>([]);
  const push = useCallback((text: string, err?: boolean) => {
    const id = Date.now() + Math.random();
    setList(l => [...l, { id, text, err }]);
    setTimeout(() => setList(l => l.filter(t => t.id !== id)), err ? 7000 : 3500);
  }, []);
  const value = useMemo(() => ({ ok: (t: string) => push(t), err: (e: unknown) => push(errorText(e), true) }), [push]);
  return <ToastCtx.Provider value={value}>{children}<div className="toasts" role="status" aria-live="polite">{list.map(t => <div key={t.id} className={'toast' + (t.err ? ' err' : '')}>{t.text}</div>)}</div></ToastCtx.Provider>;
}
export const useToast = () => useContext(ToastCtx);

/* ---------- Modal ---------- */
/** Janelas abertas, da mais antiga para a mais recente: Esc fecha só a de cima. */
const modalStack: object[] = [];
export function Modal({ title, onClose, children, foot, size }: { title: ReactNode; onClose: () => void; children: ReactNode; foot?: ReactNode; size?: 'wide' | 'narrow' }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input:not([type=hidden]),select,textarea,button.primary');
    first?.focus();
    const token = {}; modalStack.push(token);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && modalStack[modalStack.length - 1] === token) { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); modalStack.splice(modalStack.indexOf(token), 1); prev?.focus?.(); };
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'modal' + (size ? ' ' + size : '')} role="dialog" aria-modal="true" ref={ref}>
        <div className="modal-head"><h3>{title}</h3><button className="icon-btn" onClick={onClose} aria-label="Fechar"><Icon name="x" /></button></div>
        <div className="modal-body">{children}</div>
        {foot && <div className="modal-foot">{foot}</div>}
      </div>
    </div>
  );
}

/** Pede justificativa (mín. 3 caracteres) antes de uma ação destrutiva ou sensível. */
export function ReasonDialog({ title, label = 'Justificativa', confirm = 'Confirmar', danger, min = 3, onConfirm, onClose, children }: { title: string; label?: string; confirm?: string; danger?: boolean; min?: number; onConfirm: (reason: string) => Promise<unknown>; onClose: () => void; children?: ReactNode }) {
  const [reason, setReason] = useState(''); const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const go = async () => {
    if (min > 0 && reason.trim().length < min) { setErr(`Escreva ao menos ${min} caracteres.`); return; }
    setBusy(true); setErr('');
    try { await onConfirm(reason.trim()); onClose(); } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} size="narrow" foot={<><button className="btn" onClick={onClose}>Cancelar</button><button className={'btn ' + (danger ? 'danger' : 'primary')} disabled={busy} onClick={go}>{busy ? 'Aguarde...' : confirm}</button></>}>
      <div className="stack">
        {children}
        <Field label={label} required={min > 0} error={err}><textarea className={'input' + (err ? ' invalid' : '')} value={reason} onChange={e => setReason(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}

/* ---------- Campos ---------- */
/** Tela atual, para escolher a ajuda certa de cada campo (definida nas rotas). */
export const TipScope = createContext('');

/**
 * Ícone "?" com a explicação do campo ao passar o mouse (ou tocar, no celular).
 * Para leitores de tela, a mesma explicação vira a descrição do campo (aria-describedby), sem repetir no nome.
 */
export function Tip({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect(); if (!r) return;
    const w = Math.min(300, window.innerWidth - 16);
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2));
    const below = r.top < 120;
    setPos({ top: below ? r.bottom + 8 : r.top - 8, left, below });
  };
  return (
    <span ref={ref} className="tip" aria-hidden="true" onMouseEnter={show} onMouseLeave={() => setPos(null)}
      onClick={e => { e.preventDefault(); e.stopPropagation(); if (pos) setPos(null); else show(); }}>
      {pos && <span className={'tip-text' + (pos.below ? ' below' : '')} style={{ top: pos.top, left: pos.left }}>{text}</span>}
    </span>
  );
}

/**
 * Botão que, quando não pode ser usado, diz o porquê: fica esmaecido e mostra o motivo
 * ao passar o mouse, ao focar com o teclado ou ao clicar (no celular). Sem motivo, funciona como um botão comum.
 */
export function Btn({ why, className = 'btn', children, onClick, ...rest }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> & { why?: string | false | null }) {
  const ref = useRef<HTMLButtonElement>(null); const tid = useId();
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);
  const blocked = !!why;
  const show = () => {
    const r = ref.current?.getBoundingClientRect(); if (!r || !blocked) return;
    const w = Math.min(300, window.innerWidth - 16);
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2));
    const below = r.top < 120;
    setPos({ top: below ? r.bottom + 8 : r.top - 8, left, below });
  };
  useEffect(() => { if (!blocked) setPos(null); }, [blocked]);
  return (
    <button ref={ref} type="button" {...rest} className={className + (blocked ? ' is-blocked' : '')} aria-disabled={blocked || undefined} aria-describedby={blocked ? tid : rest['aria-describedby']}
      onMouseEnter={show} onMouseLeave={() => setPos(null)} onFocus={show} onBlur={() => setPos(null)}
      onClick={e => { if (blocked) { e.preventDefault(); show(); return; } onClick?.(e); }}>
      {children}
      {blocked && <span id={tid} hidden>{why}</span>}
      {pos && blocked && <span role="tooltip" className={'tip-text why' + (pos.below ? ' below' : '')} style={{ top: pos.top, left: pos.left }}>{why}</span>}
    </button>
  );
}

/** Lista visível dos botões bloqueados e o porquê de cada um (aparece perto dos botões de avanço). */
export function WhyList({ items }: { items: { label: string; why: string }[] }) {
  const groups: { labels: string[]; why: string }[] = [];
  for (const x of items) { const g = groups.find(y => y.why === x.why); if (g) g.labels.push(x.label); else groups.push({ labels: [x.label], why: x.why }); }
  if (!groups.length) return null;
  return <ul className="why-list" aria-label="Por que alguns botões estão bloqueados">{groups.map(g => <li key={g.why}><b>{g.labels.join(', ')}:</b> {g.why}</li>)}</ul>;
}

/** Campo com rótulo e ajuda (?). Use group para conjuntos de caixas de seleção (vira um grupo, não um rótulo). */
export function Field({ label, required, help, error, children, full, group, tip }: { label: string; required?: boolean; help?: ReactNode; error?: string; children: ReactNode; full?: boolean; group?: boolean; tip?: string }) {
  const scope = useContext(TipScope); const tid = useId();
  const text = tip ?? tipFor(scope, label);
  const control = text && isValidElement(children) ? cloneElement(children as ReactElement<Record<string, unknown>>, { 'aria-describedby': tid }) : children;
  const inner = <>
    <span className="field-label">{label}{required && <span className="req"> *</span>}{text && <Tip text={text} />}</span>
    {text && <span id={tid} hidden>{text}</span>}
    {control}
    {error ? <span className="field-help" style={{ color: 'var(--crit)' }} role="alert">{error}</span> : help ? <span className="field-help">{help}</span> : null}
  </>;
  const cls = 'field' + (full ? ' full' : '');
  return group ? <div className={cls} role="group" aria-label={label}>{inner}</div> : <label className={cls}>{inner}</label>;
}

/** Data no padrão brasileiro. Valor interno em AAAA-MM-DD; aceita digitação DD/MM/AAAA ou escolha no calendário. */
export function DateInput({ value, onChange, invalid, ...rest }: { value: string | null | undefined; onChange: (iso: string | null) => void; invalid?: boolean; placeholder?: string; disabled?: boolean; 'aria-label'?: string }) {
  const [text, setText] = useState(fmtDate(value));
  const [bad, setBad] = useState(false); const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => { setText(fmtDate(value)); setBad(false); }, [value]);
  const commit = (t: string) => {
    if (!t.trim()) { setBad(false); onChange(null); return; }
    const iso = parseBR(t); setBad(!iso); if (iso) onChange(iso);
  };
  const mask = (s: string) => { const d = s.replace(/\D/g, '').slice(0, 8); return d.length > 4 ? `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}` : d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; };
  return (
    <span className="date-field">
      <input className={'input mono' + (bad || invalid ? ' invalid' : '')} inputMode="numeric" placeholder="DD/MM/AAAA" value={text} {...rest}
        onChange={e => { const m = mask(e.target.value); setText(m); if (m.length === 10) commit(m); else if (!m) commit(''); }} onBlur={() => commit(text)} />
      <button type="button" ref={btn} className="date-btn" disabled={rest.disabled} aria-label="Escolher a data no calendário" aria-haspopup="dialog" aria-expanded={open} title="Escolher no calendário"
        onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}><Icon name="calendar" /></button>
      {open && <CalendarPopover anchor={btn.current} value={parseBR(text) ?? value ?? null} onPick={iso => { setOpen(false); setText(fmtDate(iso)); setBad(false); onChange(iso); btn.current?.focus(); }} onClose={() => { setOpen(false); btn.current?.focus(); }} />}
    </span>
  );
}

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const iso = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const localToday = () => { const t = new Date(); return iso(t.getFullYear(), t.getMonth(), t.getDate()); };
/** Calendário de um mês, aberto pelo botão do campo de data. Setas do teclado mudam o dia; Enter escolhe; Esc fecha. */
function CalendarPopover({ anchor, value, onPick, onClose }: { anchor: HTMLElement | null; value: string | null; onPick: (iso: string) => void; onClose: () => void }) {
  const start = value ?? localToday();
  const [cursor, setCursor] = useState(start);
  const y = Number(cursor.slice(0, 4)), m = Number(cursor.slice(5, 7)) - 1;
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });
  useEffect(() => {
    const place = () => {
      const r = anchor?.getBoundingClientRect(); const el = box.current; if (!r || !el) return;
      const w = el.offsetWidth, h = el.offsetHeight;
      const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w));
      const top = r.bottom + 6 + h > window.innerHeight - 8 && r.top - 6 - h > 8 ? r.top - 6 - h : r.bottom + 6;
      setPos({ top, left });
    };
    place(); window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [anchor]);
  useEffect(() => {
    const out = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node) && !anchor?.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', out); return () => document.removeEventListener('mousedown', out);
  }, [anchor, onClose]);
  useEffect(() => { box.current?.querySelector<HTMLButtonElement>(`button[data-d="${cursor}"]`)?.focus(); }, [cursor]);
  const move = (days: number) => { const d = new Date(Date.UTC(y, m, Number(cursor.slice(8, 10)) + days)); setCursor(d.toISOString().slice(0, 10)); };
  const goMonth = (n: number) => { const last = new Date(Date.UTC(y, m + n + 1, 0)).getUTCDate(); const d = new Date(Date.UTC(y, m + n, Math.min(Number(cursor.slice(8, 10)), last))); setCursor(d.toISOString().slice(0, 10)); };
  const first = new Date(Date.UTC(y, m, 1)).getUTCDay(); const days = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const cells: (number | null)[] = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const today = localToday();
  const onKey = (e: React.KeyboardEvent) => {
    const k: Record<string, () => void> = { ArrowLeft: () => move(-1), ArrowRight: () => move(1), ArrowUp: () => move(-7), ArrowDown: () => move(7), PageUp: () => goMonth(-1), PageDown: () => goMonth(1), Escape: onClose };
    if (k[e.key]) { e.preventDefault(); e.stopPropagation(); k[e.key]!(); }
  };
  return (
    <div className="cal-pop" role="dialog" aria-label="Calendário" ref={box} style={{ top: pos.top, left: pos.left }} onKeyDown={onKey}>
      <div className="cal-pop-head">
        <button type="button" className="icon-btn sm" aria-label="Ano anterior" onClick={() => goMonth(-12)}>«</button>
        <button type="button" className="icon-btn sm" aria-label="Mês anterior" onClick={() => goMonth(-1)}>‹</button>
        <strong aria-live="polite">{MONTHS[m]} de {y}</strong>
        <button type="button" className="icon-btn sm" aria-label="Próximo mês" onClick={() => goMonth(1)}>›</button>
        <button type="button" className="icon-btn sm" aria-label="Próximo ano" onClick={() => goMonth(12)}>»</button>
      </div>
      <div className="cal-pop-grid" role="grid">
        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => <span key={i} className="dow" aria-hidden="true">{d}</span>)}
        {cells.map((d, i) => d === null ? <span key={i} /> : (() => {
          const v = iso(y, m, d);
          return <button type="button" key={i} data-d={v} tabIndex={v === cursor ? 0 : -1} aria-label={`${d} de ${MONTHS[m]!.toLowerCase()} de ${y}`} aria-pressed={v === value}
            className={(v === value ? 'sel ' : '') + (v === today ? 'today ' : '') + (i % 7 === 0 || i % 7 === 6 ? 'we' : '')} onClick={() => onPick(v)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(v); } }}>{d}</button>;
        })())}
      </div>
      <div className="cal-pop-foot"><button type="button" className="btn sm" onClick={() => onPick(today)}>Hoje</button><span className="small muted">Setas mudam o dia</span></div>
    </div>
  );
}

/** Número no padrão brasileiro (1.234,56). */
export function NumberInput({ value, onChange, decimals = 2, invalid, ...rest }: { value: number | null | undefined; onChange: (n: number | null) => void; decimals?: number; invalid?: boolean; placeholder?: string; disabled?: boolean; min?: number; max?: number; 'aria-label'?: string }) {
  const show = (v: number | null | undefined) => v === null || v === undefined ? '' : fmtNum(v, Number.isInteger(v) ? 0 : decimals).replace(/\./g, '');
  const [text, setText] = useState(show(value)); const [bad, setBad] = useState(false);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) { setText(show(value)); setBad(false); } }, [value]);
  return <input className={'input num' + (bad || invalid ? ' invalid' : '')} inputMode="decimal" value={text} {...rest}
    onFocus={() => { focused.current = true; }}
    onChange={e => { setText(e.target.value); const n = parseNum(e.target.value); const ok = n === null || !Number.isNaN(n); setBad(!ok); if (ok) onChange(n); }}
    onBlur={() => { focused.current = false; if (!bad) setText(show(value)); }} />;
}

export function Select<T extends string>({ value, onChange, options, empty, ...rest }: { value: T | null | undefined; onChange: (v: T | null) => void; options: { value: T; label: string }[]; empty?: string; disabled?: boolean; 'aria-label'?: string; 'aria-describedby'?: string; className?: string }) {
  return (
    <select className={'input ' + (rest.className ?? '')} value={value ?? ''} disabled={rest.disabled} aria-label={rest['aria-label']} aria-describedby={rest['aria-describedby']} onChange={e => onChange((e.target.value || null) as T | null)}>
      {empty !== undefined && <option value="">{empty}</option>}
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/* ---------- Abas, selos, vazio ---------- */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { value: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return <div className="tabs" role="tablist">{tabs.map(t => <button key={t.value} role="tab" aria-selected={t.value === value} className={t.value === value ? 'on' : ''} onClick={() => onChange(t.value)}>{t.label}</button>)}</div>;
}
/** Card de indicador. Com onClick vira um atalho: filtra a lista ou abre a tela que explica o número. */
export function Kpi({ label, value, sub, tone, onClick, active, hint }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'crit' | 'ok'; onClick?: () => void; active?: boolean; hint?: string }) {
  const inner = <>
    <span className="kpi-label">{label}</span>
    <span className="kpi-value" style={{ color: tone === 'crit' ? 'var(--crit)' : tone === 'ok' ? 'var(--ok)' : undefined }}>{value}</span>
    {sub && <span className="kpi-sub">{sub}</span>}
  </>;
  if (!onClick) return <div className="kpi">{inner}</div>;
  return (
    <button type="button" className={'kpi kpi-btn' + (active ? ' on' : '')} aria-pressed={active} onClick={onClick}>
      {inner}<span className="kpi-go">{active ? 'Filtrando a lista · clique para limpar' : `${hint ?? 'Ver lista'} ›`}</span>
    </button>
  );
}
export const Badge = ({ tone, children }: { tone?: 'ok' | 'warn' | 'crit' | 'info' | 'accent'; children: ReactNode }) => <span className={'badge' + (tone ? ' ' + tone : '')}>{children}</span>;
export const Empty = ({ title, children }: { title: string; children?: ReactNode }) => <div className="empty"><strong>{title}</strong>{children}</div>;
export const Loading = ({ text = 'Carregando...' }: { text?: string }) => <div className="loading" aria-busy="true">{text}</div>;
export function ErrorBox({ error, retry }: { error: unknown; retry?: () => void }) {
  return <div className="callout crit" role="alert">{errorText(error)} {retry && <button className="btn sm" onClick={retry}>Tentar de novo</button>}</div>;
}
export function Bar({ value, mark, tone }: { value: number; mark?: number; tone?: 'ok' | 'warn' | 'crit' }) {
  return <div className={'bar' + (tone ? ' ' + tone : '')} role="img" aria-label={`${fmtNum(value, 1)}%`}><i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />{mark !== undefined && <b style={{ left: `${Math.max(0, Math.min(100, mark))}%` }} />}</div>;
}

/* ---------- Tabela com busca, ordenação e paginação ---------- */
export interface Col<T> { key: string; label: ReactNode; render?: (r: T) => ReactNode; sort?: (r: T) => string | number | null; num?: boolean; className?: string }
export function DataTable<T extends { id: string }>({ rows, cols, onRow, search, pageSize = 50, empty = 'Nenhum registro.', rowClass, initialSort }: { rows: T[]; cols: Col<T>[]; onRow?: (r: T) => void; search?: (r: T) => string; pageSize?: number; empty?: ReactNode; rowClass?: (r: T) => string; initialSort?: string }) {
  const [q, setQ] = useState(''); const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(initialSort ? { key: initialSort, dir: 1 } : null); const [page, setPage] = useState(0);
  const sid = useId();
  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim() && search) { const n = normalize(q.trim()); out = out.filter(r => normalize(search(r)).includes(n)); }
    if (sort) {
      const c = cols.find(c => c.key === sort.key); const f = c?.sort;
      if (f) out = [...out].sort((a, b) => { const x = f(a), y = f(b); if (x === y) return 0; if (x === null) return 1; if (y === null) return -1; return (x < y ? -1 : 1) * sort.dir; });
    }
    return out;
  }, [rows, q, sort, cols, search]);
  useEffect(() => setPage(0), [q, rows.length]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const shown = filtered.slice(page * pageSize, page * pageSize + pageSize);
  return (
    <div>
      {search && <div className="filters" style={{ padding: '10px 12px 0' }}><input id={sid} className="input search" placeholder="Buscar..." aria-label="Buscar" value={q} onChange={e => setQ(e.target.value)} /><span className="small muted">{filtered.length} de {rows.length}</span></div>}
      <div className="table-wrap">
        <table className="t">
          <thead><tr>{cols.map(c => (
            <th key={c.key} className={(c.sort ? 'sortable ' : '') + (c.num ? 'num' : '')} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
              onClick={c.sort ? () => setSort(s => s?.key === c.key ? { key: c.key, dir: s.dir === 1 ? -1 : 1 } : { key: c.key, dir: 1 }) : undefined}>
              {c.label}{sort?.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
            </th>))}</tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={cols.length}><div className="empty">{empty}</div></td></tr>}
            {shown.map(r => (
              <tr key={r.id} className={(onRow ? 'clickable ' : '') + (rowClass?.(r) ?? '')} onClick={onRow ? () => onRow(r) : undefined} tabIndex={onRow ? 0 : undefined} onKeyDown={onRow ? e => { if (e.key === 'Enter') onRow(r); } : undefined}>
                {cols.map(c => <td key={c.key} className={(c.num ? 'num ' : '') + (c.className ?? '')}>{c.render ? c.render(r) : String((r as Record<string, unknown>)[c.key] ?? '')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && <div className="pager"><button className="btn sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</button><span>Página {page + 1} de {pages}</span><button className="btn sm" disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Próxima</button></div>}
    </div>
  );
}

/** Estado de formulário simples com marcação de alterações. */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [v, setV] = useState<T>(initial);
  const set = useCallback(<K extends keyof T>(k: K, val: T[K]) => setV(o => ({ ...o, [k]: val })), []);
  const changed = useMemo(() => Object.fromEntries(Object.entries(v).filter(([k, x]) => JSON.stringify(x) !== JSON.stringify(initial[k]))) as Partial<T>, [v, initial]);
  return { v, set, setV, changed };
}
